import { CancelButton, SaveButton } from '@core/components/Buttons';
import { CustomExpandedPanel } from '@core/components/CustomExpandedPanel';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { DateInput, TextInput, TextLabel } from '@core/components/Inputs';
import ReadOnlyInput from '@core/components/Inputs/ReadOnlyInput';
import { useApiUtils } from '@core/util/apiUtils';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@material-ui/core';
import InputMonetario from 'app/components/Input/InputMonetario';
import SelectConta from 'app/components/Select/SelectConta';
import { cancelarAlert } from 'app/services/alerts';
import contaReceberApi from 'app/services/api/contaReceberApi';
import * as Actions from 'app/store/actions';
import { getCurrency } from 'app/utils/monetarioUtil';
import { FormikProvider, useFormik, useFormikContext } from 'formik';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { ContaReceberContext } from '../../contaReceberContext';
import ContaReceberRecebimentosRealizados from '../ContaReceberRecebimentosRealizados';
import { contaReceberModais } from './contaReceberModais';

const ORIGENS_LANCAMENTO = {
  CONTA_RECEBER: 'Contas a receber',
  CONTRATO: 'Contratos',
  ADESAO: 'Contratos',
  VENDA: 'Vendas',
  VENDA_CONTRATO: 'Venda e contrato',
};

const RenderInfo = ({ values }) => {
  if (values?.multiplo && values?.ids?.length > 1) {
    return (
      <Grid item xs={12}>
        <TextLabel
          fullWidth
          id="valorPago"
          label="Valor total das contas a receber selecionadas (R$)"
          value={getCurrency(values.somatorioSelecao).replace('R$', '')}
        />
      </Grid>
    );
  }
  return (
    <>
      <Grid item xs={12} sm={4}>
        <TextLabel id="cliente" label="Cliente" fullWidth value={values?.cliente?.pessoa?.nome} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextLabel
          id="valorBruto"
          label="Valor (R$) "
          fullWidth
          value={getCurrency(values?.valorBruto).replace('R$', '')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextLabel
          id="origemLancamento"
          label="Origem do lançamento"
          fullWidth
          value={ORIGENS_LANCAMENTO[values?.contaReceber?.origemLancamento]}
        />
      </Grid>
    </>
  );
};
const FormBaixa = () => {
  const [contaReceber] = useContext(ContaReceberContext).contaReceberAtual;
  const [, setLoading] = useContext(ContaReceberContext).loading;
  const [formaRecebimento, setFormaRecebimento] = useState('');

  const formikContext = useFormikContext();
  const { values, handleChange, handleBlur, errors, touched, setFieldValue, submitCount } = formikContext;

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const variasContas = values?.contas?.length > 1;

  const disabledSeForBaixaParcial =
    values?.situacao === 'PARCIAL' || values?.ids?.some((contaReceber) => contaReceber.situacao === 'PARCIAL');

  const _errors = {
    baixa: {
      valorRecebido: (submitCount > 0 || touched?.baixa?.valorRecebido) && errors?.baixa?.valorRecebido,
    },
  };

  const aplicarAcrescimos = (acrescimos) => {
    handleChange({ target: { id: 'baixa.juros.valorBruto', value: acrescimos.valorJuros } });
    handleChange({ target: { id: 'baixa.multa.valorBruto', value: acrescimos.valorMulta } });
    handleChange({
      target: { id: 'baixa.correcaoMonetaria.valorBruto', value: acrescimos.valorCorrecaoMonetaria },
    });
  };

  const buscarAcrecimos = async (dataPgamento) => {
    setLoading(true);
    try {
      const params = {
        valor: contaReceber.valorBruto,
        vencimento: contaReceber.dataVencimento,
        pagamento: dataPgamento?.format('YYYY-MM-DD'),
      };

      const response = await contaReceberApi.buscarAcrescimos(params);
      aplicarAcrescimos(response.data);
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeIncluirJurosMulta = (event) => {
    handleChange(event);
    if (!values?.multiplo) {
      if (event.target.id.includes('dataRecebimento')) {
        if (event.target.value && values?.baixa.acrescimosDescontos) {
          buscarAcrecimos(event?.target?.value);
        }
      } else if (event.target.id.includes('acrescimosDescontos')) {
        if (!values?.baixa.acrescimosDescontos && values?.baixa.dataRecebimento) {
          buscarAcrecimos(values.baixa.dataRecebimento);
        } else {
          setFieldValue('baixa.juros', undefined);
          setFieldValue('baixa.multa', undefined);
          setFieldValue('baixa.desconto', undefined);
          setFieldValue('baixa.correcaoMonetaria', undefined);
        }
      }
    }
  };

  const getSomatorioSelecao = () => {
    let total = 0;
    if (values?.ids) {
      values.ids.forEach((receita) => {
        total += receita.valor;
      });
    }

    return total;
  };

  useEffect(() => {
    if (values?.valorEmAbertoParcelasAnteriores === values?.valorBruto) {
      values.baixa.valorRecebido = values?.valorBruto || 0;
    } else {
      values.baixa.valorRecebido = values?.valorEmAbertoParcelasAnteriores || 0;
    }
    // Efeito de montagem: roda uma única vez. Incluir as dependências reexecutaria a carga
    // inicial a cada render (loop de chamadas à API).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getVendaOuContratoCorrespondente = async () => {
      let vendaOuContrato;
      if (contaReceber.origemLancamento === 'VENDA') {
        vendaOuContrato = await contaReceberApi.buscarVendaPorContaReceberId(contaReceber.id);
      } else if (contaReceber.origemLancamento === 'CONTRATO') {
        vendaOuContrato = await contaReceberApi.buscarContratoPorContaReceberId(contaReceber.id);
      }

      let formaRecebimento = vendaOuContrato?.data?.pagamento?.formaRecebimento || vendaOuContrato?.data?.formaCobranca;

      if (
        vendaOuContrato?.data?.recebimentoDiferentePrimeiraCobranca &&
        vendaOuContrato?.data?.contasReceber[0]?.receitaId === contaReceber.id
      ) {
        formaRecebimento = vendaOuContrato?.data?.formaRecebimentoPrimeiraCobranca;
      }

      if (formaRecebimento) {
        setFieldValue('baixa.formaRecebimento', formaRecebimento);
        setFormaRecebimento(formaRecebimento);
      }
    };

    getVendaOuContratoCorrespondente();
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contaReceber]);

  const onChange = (event) => {
    setFormaRecebimento(event.target.value);
    handleChange({ target: { id: 'baixa.formaRecebimento', value: event.target.value } });
  };

  /*
    O "Valor em aberto" deve ser calculado conforme o cálculo abaixo:

    Valor em aberto = Valor em aberto das parcelas anteriores + juros + multa + correção - desconto - valorRecebido
  */
  const jurosValorBruto = values?.baixa?.juros?.valorBruto;
  const multaValorBruto = values?.baixa?.multa?.valorBruto;
  const correcaoMonetariaValorBruto = values?.baixa?.correcaoMonetaria?.valorBruto;
  const descontoValorBruto = values?.baixa?.desconto?.valorBruto;
  const baixaValorRecebido = values?.baixa?.valorRecebido;
  useEffect(() => {
    const valorEmAbertoParcelasAnteriores = values?.valorEmAbertoParcelasAnteriores || 0;
    const valorRecebido = baixaValorRecebido || 0;

    const valorEmAberto = valorEmAbertoParcelasAnteriores - valorRecebido;

    handleChange({ target: { id: 'baixa.valorEmAberto', value: valorEmAberto } });
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jurosValorBruto, multaValorBruto, correcaoMonetariaValorBruto, descontoValorBruto, baixaValorRecebido]);

  const [expanded, setExpanded] = useState('');

  const handleExpandedChange = (key, value) => {
    if (value) {
      setExpanded(key);
    } else {
      setExpanded(false);
    }
  };

  const getSomatorioValorBrutoCategoriasList = () => {
    const valorBrutoSomatorio =
      values?.categorias?.reduce((acc, item) => {
        return acc + item.valorBruto;
      }, 0) || 0;

    let valorEmAberto = valorBrutoSomatorio;
    return valorEmAberto;
  };

  const exibirValorEmAberto = () => {
    let valorTotal = values?.baixa?.valorRecebido || 0;
    if (contaReceber?.baixas?.length > 0) {
      for (let i = 0; i < contaReceber?.baixas?.length; i++) {
        if (contaReceber?.baixas[i]?.juros?.valorBruto) {
          valorTotal -= contaReceber?.baixas[i]?.juros?.valorBruto;
        }
        if (contaReceber?.baixas[i]?.multa?.valorBruto) {
          valorTotal -= contaReceber?.baixas[i]?.multa?.valorBruto;
        }
        if (contaReceber?.baixas[i]?.correcaoMonetaria?.valorBruto) {
          valorTotal -= contaReceber?.baixas[i]?.correcaoMonetaria?.valorBruto;
        }
        if (contaReceber?.baixas[i]?.desconto?.valorBruto) {
          valorTotal += contaReceber?.baixas[i]?.desconto?.valorBruto;
        }

        valorTotal += contaReceber?.baixas[i]?.valorPago;
      }
    }
    return values?.baixa && valorTotal !== getSomatorioValorBrutoCategoriasList();
  };

  const calcularValorRecebido = (values) => {
    const juros = values?.baixa?.juros?.valorBruto || 0;
    const multa = values?.baixa?.multa?.valorBruto || 0;
    const correcaoMonetaria = values?.baixa?.correcaoMonetaria?.valorBruto || 0;
    const desconto = values?.baixa?.desconto?.valorBruto || 0;

    let valorAcrescimosDesconco = juros + multa + correcaoMonetaria - desconto;
    let valorTotal = values?.baixa?.valorRecebido + valorAcrescimosDesconco;

    return isNaN(valorTotal) ? '-' : getCurrency(valorTotal);
  };

  return (
    <Grid container spacing={3}>
      <RenderInfo
        values={{ ...values, somatorioSelecao: getSomatorioSelecao(), contaReceber }}
        variasContas={variasContas}
      />
      <Grid item xs={12} sm={6}>
        <DateInput
          fullWidth
          required
          id="baixa.dataRecebimento"
          label="Data do recebimento"
          value={values?.baixa?.dataRecebimento}
          onChange={handleChangeIncluirJurosMulta}
          onBlur={handleBlur}
          touched={touched?.baixa?.dataRecebimento}
          error={errors?.baixa?.dataRecebimento}
          helperText={errors?.baixa?.dataRecebimento}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DateInput
          fullWidth
          required
          id="baixa.dataLiquidacao"
          label="Data da liquidação"
          value={values?.baixa?.dataLiquidacao}
          onChange={handleChangeIncluirJurosMulta}
          onBlur={handleBlur}
          touched={touched?.baixa?.dataLiquidacao}
          error={errors?.baixa?.dataLiquidacao}
          helperText={errors?.baixa?.dataLiquidacao}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <SelectConta
          id="baixa.conta"
          name="baixa.conta"
          fullWidth
          label="Conta"
          value={values?.baixa?.conta}
          onChange={(event) => {
            handleChange(event);
            handleChange({
              target: {
                id: 'baixa.formaPagamento',
                value: undefined,
              },
            });
          }}
          onBlur={handleBlur}
          required
          touched={touched?.baixa?.conta}
          error={errors?.baixa?.conta}
          helperText={errors?.baixa?.conta}
          disabled={disabledSeForBaixaParcial}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl variant="outlined" style={{ width: '100%' }}>
          <InputLabel id="baixa.formaRecebimento" required shrink>
            Forma de recebimento
          </InputLabel>
          <Select
            id="baixa.formaRecebimento"
            name={'baixa.formaRecebimento'}
            label="Forma de recebimento"
            labelId="baixa.formaRecebimento"
            value={formaRecebimento}
            onChange={onChange}
            onBlur={handleBlur}
            error={errors?.baixa?.formaRecebimento}
            helperText={errors?.baixa?.formaRecebimento}
            touched={touched?.baixa?.formaRecebimento}
            notched
            required
          >
            <MenuItem value="BOLETO">Boleto</MenuItem>
            <MenuItem value="CARTAO_CREDITO">Cartão de crédito</MenuItem>
            <MenuItem value="CARTAO_DEBITO">Cartão de débito</MenuItem>
            <MenuItem value="CHEQUE">Cheque</MenuItem>
            <MenuItem value="DINHEIRO">Dinheiro</MenuItem>
            <MenuItem value="TED_DOC">TED/DOC</MenuItem>
            <MenuItem value="PIX">PIX</MenuItem>
            <MenuItem value="OUTRAS">Outras</MenuItem>
          </Select>
          <FormHelperText style={{ color: '#f44336' }}>{errors?.baixa?.formaRecebimento || ''}</FormHelperText>
        </FormControl>
      </Grid>
      {values?.baixa?.formaPagamento === 'CHEQUE' && (
        <Grid item xs={12} sm={6}>
          <TextInput
            fullWidth
            required
            id="baixa.numeroCheque"
            label="Número do cheque"
            value={values?.baixa?.numeroCheque}
            onChange={handleChange}
            maxLength={20}
            pattern={/^\d*$/}
          />
        </Grid>
      )}
      {!values?.multiplo && (
        <>
          <Grid item xs={12} sm={6}>
            <InputMonetario
              fullWidth
              id="baixa.valorRecebido"
              label="Valor (R$)"
              onBlur={handleBlur}
              value={values?.baixa?.valorRecebido}
              handleChange={handleChange}
              touched={!!_errors?.baixa?.valorRecebido}
              error={_errors?.baixa?.valorRecebido}
              helperText={_errors?.baixa?.valorRecebido}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              label="Incluir acréscimos e descontos"
              control={
                <Switch
                  id="baixa.acrescimosDescontos"
                  name="baixa.acrescimosDescontos"
                  value={values?.acrescimosDescontos}
                  checked={values?.acrescimosDescontos}
                  onChange={handleChangeIncluirJurosMulta}
                  onBlur={handleBlur}
                  color="primary"
                />
              }
            />
          </Grid>
          {values?.baixa?.acrescimosDescontos && (
            <>
              <Grid item xs={12} sm={6}>
                <InputMonetario
                  fullWidth
                  id="baixa.juros.valorBruto"
                  label="Juros (R$)"
                  onBlur={handleBlur}
                  value={values?.baixa?.juros?.valorBruto}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputMonetario
                  fullWidth
                  id="baixa.multa.valorBruto"
                  label="Multa (R$)"
                  onBlur={handleBlur}
                  value={values?.baixa?.multa?.valorBruto}
                  handleChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputMonetario
                  fullWidth
                  id="baixa.desconto.valorBruto"
                  label="Desconto (R$)"
                  onBlur={handleBlur}
                  value={values?.baixa?.desconto?.valorBruto}
                  handleChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputMonetario
                  fullWidth
                  id="baixa.correcaoMonetaria.valorBruto"
                  label="Correção monetária (R$)"
                  onBlur={handleBlur}
                  value={values?.baixa?.correcaoMonetaria?.valorBruto}
                  handleChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ReadOnlyInput
                  fullWidth
                  id="valorRecebido"
                  label="Valor recebido (R$)"
                  onBlur={handleBlur}
                  value={calcularValorRecebido(values)}
                  handleChange={handleChange}
                />
              </Grid>
            </>
          )}

          {/*
          O "Valor em aberto" deve ser calculado conforme o cálculo abaixo:
    
          Valor em aberto = Valor em aberto das parcelas anteriores + juros + multa + correção - desconto - valorRecebido 
          */}
          {exibirValorEmAberto() && (
            <Grid item xs={12} sm={6}>
              <ReadOnlyInput
                fullWidth
                id="baixa.valorEmAberto"
                label="Valor em aberto (R$)"
                value={getCurrency(values?.baixa?.valorEmAberto)}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={12}>
            {values?.baixa?.valorEmAberto < 0 && (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography style={{ fontSize: 15, fontWeight: 500, color: '#f00' }}>
                  Diferença: {getCurrency(values?.baixa?.valorEmAberto)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={12}>
        {contaReceber?.baixas?.length > 0 && (
          <CustomExpandedPanel
            title="Recebimentos realizados"
            expanded={expanded === 'recebimentos'}
            onChange={(_, value) => handleExpandedChange('recebimentos', value)}
          >
            <ContaReceberRecebimentosRealizados
              baixas={contaReceber?.baixas}
              showTitle={false}
              showCancelarBaixa={false}
            />
          </CustomExpandedPanel>
        )}
      </Grid>
    </Grid>
  );
};

const ContasReceberBaixaModal = ({ onSubmit }) => {
  const [selecao] = useContext(ContaReceberContext).selecao;
  const [contaReceber, setContaReceber] = useContext(ContaReceberContext).contaReceberAtual;
  const [modal, setModal] = useContext(ContaReceberContext).modal;
  const [search] = useContext(ContaReceberContext).search;
  const [page] = useContext(ContaReceberContext).page;
  const [sort] = useContext(ContaReceberContext).sort;
  const [filtroAvancado] = useContext(ContaReceberContext).filtroAvancado;

  const schemaDadosBaixa = yup.object().shape({
    baixa: yup.object().shape({
      dataRecebimento: yup
        .date()
        .nullable()
        .required(mensagemCampoObrigatorio('a data do recebimento.')),
      dataLiquidacao: yup
        .date()
        .nullable()
        .required(mensagemCampoObrigatorio('a data da liquidação.'))
        .min(yup.ref('dataRecebimento'), 'A data de liquidação não pode ser inferior à data de recebimento.'),
      conta: yup
        .object()
        .nullable()
        .test('conta', mensagemCampoObrigatorio('a conta.'), function getIdConta(conta) {
          return conta?.id;
        }),
      formaRecebimento: yup
        .string()
        .nullable()
        .required(mensagemCampoObrigatorio('a forma de recebimento.')),
      valorRecebido: yup.number().required(mensagemCampoObrigatorio('o valor recebido')),
    }),
  });

  const onSubmitForm = async (values) => {
    if (!(values?.baixa?.valorEmAberto < 0)) {
      const res = await onSubmit(values, { q: search, page, sort, filtroAvancado });

      if (res) {
        setContaReceber({});
        setModal('');
      }
    }
  };

  const getValorEmAbertoParcelasAnteriores = (baixas, valorBruto) => {
    let valorEmAberto = 0;
    for (let i = 0; i < baixas?.length; i++) {
      const baixa = baixas[i];
      let correcoes =
        (baixa?.juros?.valorBruto || 0) +
        (baixa?.multa?.valorBruto || 0) +
        (baixa?.correcaoMonetaria?.valorBruto || 0) -
        (baixa?.desconto?.valorBruto || 0);
      valorEmAberto = valorEmAberto + correcoes - (baixa?.valorPago || 0);
    }

    return valorEmAberto + valorBruto;
  };

  const getInitialValues = (contaReceber) => ({
    ids: contaReceber?.id ? [contaReceber.id] : selecao,
    multiplo: selecao.length > 1 || false,
    baixa: { conta: contaReceber?.conta, dataRecebimento: moment(new Date()), dataLiquidacao: moment(new Date()) },
    categorias: contaReceber?.categorias,
    cliente: contaReceber?.cliente,
    valorBruto: contaReceber?.valorBruto,
    situacao: contaReceber?.situacao,
    valorEmAbertoParcelasAnteriores: getValorEmAbertoParcelasAnteriores(contaReceber?.baixas, contaReceber?.valorBruto),
  });

  const formik = useFormik({
    initialValues: getInitialValues(contaReceber),
    onSubmit: onSubmitForm,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: schemaDadosBaixa,
  });

  const selecionarConta = () => {
    if (contaReceber?.baixas?.length > 0) {
      formik.setFieldValue('baixa.conta', contaReceber?.baixas[0]?.conta);
    } else if (formik.values?.ids?.[0]?.baixas?.length > 0) {
      formik.setFieldValue('baixa.conta', formik?.values?.ids?.[0]?.baixas[0]?.conta);
    }
  };

  const setValorEmAberto = () => {
    // Quando selecionamos uma conta a receber e abrimos o modal de baixa.
    if (contaReceber?.baixas?.length > 0) {
      formik.setFieldValue(
        'baixa.valorRecebido',
        getValorEmAbertoParcelasAnteriores(contaReceber?.baixas, contaReceber?.valorBruto)
      );
    } else {
      formik.setFieldValue('baixa.valorRecebido', contaReceber?.valorBruto);
    }
  };

  useEffect(() => {
    if (contaReceber || selecao) {
      formik.resetForm({
        values: getInitialValues(contaReceber),
      });
    }
    selecionarConta();
    setValorEmAberto();
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contaReceber, selecao]);

  const { setFieldValue } = formik;
  useEffect(() => {
    const getValorEmAberto = () => {
      const valorEmAberto =
        (formik?.values?.valorEmAbertoParcelasAnteriores || 0) - (formik?.values?.baixa?.valorRecebido || 0);

      setFieldValue('baixa.valorEmAberto', valorEmAberto);
    };
    getValorEmAberto();
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFieldValue]);

  const handleCancelar = () => {
    const close = () => {
      setContaReceber({});
      setModal('');
    };

    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  return (
    <SimpleDialog open={modal === contaReceberModais.baixarContaReceber} onClose={handleCancelar}>
      <FormikProvider value={formik}>
        <FormDialogContent
          onSubmit={formik.handleSubmit}
          header={{
            title: 'Baixar conta',
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleCancelar}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="submit">
                  Salvar
                </SaveButton>
              </>
            ),
          }}
          content={<FormBaixa />}
          // contentStyle={{ padding: 0 }}
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default ContasReceberBaixaModal;
